import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import backend from '../../../api';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';

const BellIcon = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" {...props} viewBox="0 0 448 512">
    <path d="M224 0c-17.7 0-32 14.3-32 32l0 19.2C119 66 64 130.6 64 208l0 18.8c0 47-17.3 92.4-48.5 127.6l-7.4 8.3c-8.4 9.4-10.4 22.9-5.3 34.4S19.4 416 32 416l384 0c12.6 0 24-7.4 29.2-18.9s3.1-25-5.3-34.4l-7.4-8.3C401.3 319.2 384 273.9 384 226.8l0-18.8c0-77.4-55-142-128-156.8L256 32c0-17.7-14.3-32-32-32zm45.3 493.3c12-12 18.7-28.3 18.7-45.3l-64 0-64 0c0 17 6.7 33.3 18.7 45.3s28.3 18.7 45.3 18.7s33.3-6.7 45.3-18.7z" />
  </svg>
);

export default function Notifications() {
  const navigateTo = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const userRole = user?.role; // "mentor" | "mentee"

  const [chatNotifications, setChatNotifications] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [error, setError] = useState(null);

  const [loading, setLoading] = useState(true);
  const [openNotifications, setOpenNotifications] = useState(false);
  const NotificationsRef = useRef(null);

  // ---- Styles (inline) ----
  const S = {
    root: {
      position: 'relative',
      display: 'inline-block'
    },
    bellCon: {
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 38,
      height: 38,
      borderRadius: 999,
      background: '#ffffff',
      border: '1px solid #eaeaea',
      boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
      cursor: 'pointer',
      userSelect: 'none'
    },
    badge: {
      position: 'absolute',
      top: -4,
      right: -4,
      minWidth: 18,
      height: 18,
      padding: '0 5px',
      borderRadius: 9,
      background: '#ff4d4f',
      color: '#fff',
      fontSize: 11,
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      boxShadow: '0 2px 6px rgba(255,77,79,0.5)'
    },
    dropdown: {
      position: 'absolute',
      right: 0,
      top: 'calc(100% + 10px)',
      width: 380,
      maxHeight: 520,
      overflowY: 'auto',
      background: '#fff',
      border: '1px solid #eaeaea',
      boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
      borderRadius: 12,
      zIndex: 1000
    },
    banner: {
      background: 'linear-gradient(90deg, #c97975ff, #f26a63)',
      color: '#fff',
      padding: '12px 16px',
      borderTopLeftRadius: 12,
      borderTopRightRadius: 12,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    bannerTitle: { margin: 0, fontSize: 14, fontWeight: 700, letterSpacing: 0.5 },
    bannerChip: {
      background: 'rgba(255,255,255,0.2)',
      color: '#fff',
      padding: '4px 8px',
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700
    },
    body: { padding: 12, background: '#fff' },
    sectionHeaderWrap: { margin: '8px 0 10px' },
    sectionHeader: {
      margin: 0,
      fontSize: 12,
      letterSpacing: 0.6,
      textTransform: 'uppercase',
      color: '#6b7280'
    },
    divider: {
      height: 1,
      background: 'rgba(0,0,0,0.06)',
      marginTop: 6,
      borderRadius: 1
    },
    item: {
      background: '#fafafa',
      border: '1px solid #eee',
      borderRadius: 10,
      padding: '10px 12px',
      marginBottom: 10
    },
    desc: { margin: 0, fontSize: 13.5, color: '#111827', lineHeight: 1.5 },
    date: { margin: '6px 0 0', fontSize: 12, color: '#6b7280' },
    statusPill: (color) => ({
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 999,
      fontSize: 11,
      fontWeight: 700,
      color: color.text,
      background: color.bg,
      border: `1px solid ${color.border}`,
      marginLeft: 6,
      verticalAlign: 'middle'
    }),
    emptyWrap: { textAlign: 'center', padding: 24 },
    emptyText: { marginTop: 10, color: '#6b7280', fontSize: 13 }
  };

  // ---- Helpers ----
  const formatWithCommas = (dateObj) =>
    dateObj.toDateString().replace(/^(\w+) (\w+) (\d+) (\d+)$/, '$1, $2 $3, $4');

  const formatYMDToLabel = (ymd) => {
    if (!ymd) return '';
    const [y, m, d] = ymd.split('-').map(Number);
    const dt = new Date(y, (m || 1) - 1, d || 1);
    return formatWithCommas(dt); // Tue, Aug 19, 2025
  };

  const statusColors = (label) => {
    switch ((label || '').toLowerCase()) {
      case 'accepted':
        return { text: '#156c2f', bg: 'rgba(36,161,72,0.10)', border: '#bdecc8' };
      case 'canceled':
        return { text: '#7a1c1c', bg: 'rgba(224,49,49,0.10)', border: '#f1b0b0' };
      default:
        return { text: '#895f14', bg: 'rgba(250,176,5,0.10)', border: '#f4dda5' }; // Pending
    }
  };

  const toggleNotifications = (event) => {
    event.stopPropagation();
    setOpenNotifications((prev) => !prev);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (NotificationsRef.current && !NotificationsRef.current.contains(event.target)) {
        setOpenNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  async function fetchChatNotifications() {
    try {
      const response = await backend.get(`/api/chat/conversation?userId=${user._id}`);
      const conversations = response.data?.data || [];
      const unread = conversations
        .filter(
          (convo) =>
            convo.last &&
            !convo.last.read &&
            convo.last.sender?.toString() !== user._id?.toString()
        )
        .map((convo) => ({
          type: 'chat',
          partnerName: convo.partnerName || 'Unknown',
          message: convo.last.text,
          createdAt: convo.last.createdAt
        }))
        .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
        .slice(0, 5);
      setChatNotifications(unread);
    } catch (err) {
      console.error('Error fetching chat notifications:', err);
      setError('Failed to load notifications');
    }
  }

  async function fetchBookings() {
    try {
      if (userRole === 'mentor') {
        const res = await backend.get(`/bookings/mentor?mentorId=${user._id}`);
        setBookings(Array.isArray(res.data) ? res.data.slice(0, 5) : []);
      } else if (userRole === 'mentee') {
        const res = await backend.get(`/bookings/mentee?userId=${user._id}`);
        setBookings(Array.isArray(res.data) ? res.data.slice(0, 5) : []);
      } else {
        setBookings([]);
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(err.response?.data?.error || 'Failed to load bookings');
    }
  }

  useEffect(() => {
    let mounted = true;
    async function run() {
      if (!user?._id || !['mentor', 'mentee'].includes(userRole)) {
        setLoading(false);
        return;
      }
      setLoading(true);
      await Promise.all([fetchChatNotifications(), fetchBookings()]);
      if (mounted) setLoading(false);
    }
    run();
    return () => { mounted = false; };
  }, [user?._id, userRole]);

  const totalCount = (chatNotifications?.length || 0) + (bookings?.length || 0);

  // Small inline sub-component for section title
  const SectionTitle = ({ title, show }) =>
    !show ? null : (
      <div style={S.sectionHeaderWrap}>
        <h5 style={S.sectionHeader}>{title}</h5>
        <div style={S.divider} />
      </div>
    );

  return (
    <div style={S.root} ref={NotificationsRef}>
      <motion.div
        style={S.bellCon}
        onClick={toggleNotifications}
        whileTap={{ scale: 0.96 }}
      >
        <BellIcon height="20px" />
        <div style={S.badge}>{totalCount || 0}</div>
      </motion.div>

      <AnimatePresence>
        {openNotifications && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.18 }}
            style={S.dropdown}
          >
            <div style={S.banner}>
              <h5 style={S.bannerTitle}>Notifications</h5>
              <div style={S.bannerChip}>{totalCount} new</div>
            </div>

            <div style={S.body}>
              {loading ? (
                <div style={S.emptyWrap}>
                  <h5 style={{ margin: 0, color: '#374151' }}>Loading…</h5>
                </div>
              ) :  totalCount === 0 ? (
                <div style={S.emptyWrap}>
                  <img
                    src="/assets/images/empty-box.png"
                    alt="No notifications"
                    style={{ height: '5rem', opacity: 0.8 }}
                  />
                  <div style={S.emptyText}>No new notifications</div>
                </div>
              ) : (
                <>
                  {/* Messages */}
                  <SectionTitle title="Messages" show={chatNotifications.length > 0} />
                  {chatNotifications.map((item, index) => {
                    const truncated =
                      item.message?.length > 60
                        ? item.message.slice(0, 60) + '…'
                        : item.message || '';
                    const who = userRole === 'mentor' ? 'Mentee' : 'Mentor';
                    return (
                      <div key={`chat-${index}`} style={S.item}>
                        <p style={S.desc}>
                          <strong>{who} {item.partnerName}</strong> sent a new message: “{truncated}”
                        </p>
                        <p style={S.date}>
                          {new Date(item.createdAt).toLocaleString([], {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    );
                  })}

                  {/* Bookings */}
                  <SectionTitle title="Bookings" show={bookings.length > 0} />
                  {bookings.map((b, index) => {
                    const [datePart, timePart] = String(b.startsIn || '').split(' at ');
                    const formattedDate = formatYMDToLabel(datePart); // Tue, Aug 19, 2025
                    const line1 =
                      userRole === 'mentor'
                        ? `Booking with ${b.menteeName || 'Unknown User'}`
                        : `Booking with ${b.mentorName || 'Unknown Mentor'}`;
                    const statusLabel = b?.status?.label || 'Pending';
                    const c = statusColors(statusLabel);

                    return (
                      <div key={`bk-${b._id || index}`} style={S.item}>
                        <p style={S.desc}>
                          <strong>{line1}</strong>
                          <span style={S.statusPill(c)}>{statusLabel}</span>
                        </p>
                        <p style={S.date}>
                          {formattedDate}{timePart ? ` at ${timePart}` : ''}
                        </p>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
